import type { BoardState, Color, Marker } from './engine.js';

export interface StarPoint {
  x: number;
  y: number;
}

export function getStarPoints(size: number): StarPoint[] {
  if (size === 9) {
    return [
      { x: 2, y: 2 }, { x: 6, y: 2 },
      { x: 2, y: 6 }, { x: 6, y: 6 }
    ];
  }
  if (size === 13) {
    return [
      { x: 3, y: 3 }, { x: 9, y: 3 },
      { x: 6, y: 6 },
      { x: 3, y: 9 }, { x: 9, y: 9 }
    ];
  }
  if (size === 19) {
    const points: StarPoint[] = [];
    const coords = [3, 9, 15];
    for (const x of coords) {
      for (const y of coords) {
        points.push({ x, y });
      }
    }
    return points;
  }
  return [];
}

export interface DrawTheme {
  gridColor: string;
  starPointColor: string;
  blackStone: string;
  whiteStone: string;
  stoneStroke: string;
  lastMoveMarkerOnBlack: string;
  lastMoveMarkerOnWhite: string;
  hoverHighlight: string;
  /** Markup (circle/square/triangle/mark/label) color on an empty point. */
  markerColor: string;
}

export const defaultTheme: DrawTheme = {
  gridColor: '#3a2a10',
  starPointColor: '#3a2a10',
  blackStone: '#111111',
  whiteStone: '#f5f5f5',
  stoneStroke: '#222222',
  lastMoveMarkerOnBlack: '#f5f5f5',
  lastMoveMarkerOnWhite: '#111111',
  hoverHighlight: 'rgba(230, 80, 50, 0.85)',
  markerColor: '#3a2a10'
};

export interface DrawOptions {
  ctx: CanvasRenderingContext2D;
  size: number;
  cell: number;
  padding: number;
  boardPx: number;
  state: BoardState;
  lastMove: { x: number; y: number } | null;
  hover: { x: number; y: number } | null;
  turn: Color;
  marks?: Marker[];
  theme?: DrawTheme;
}

export function draw(options: DrawOptions): void {
  const { ctx, size, cell, padding, boardPx, state, lastMove, hover, turn, marks } = options;
  const theme = options.theme ?? defaultTheme;

  ctx.clearRect(0, 0, boardPx, boardPx);

  // Grid
  ctx.strokeStyle = theme.gridColor;
  ctx.lineWidth = 1;
  for (let i = 0; i < size; i++) {
    const pos = padding + i * cell;
    ctx.beginPath();
    ctx.moveTo(padding, pos);
    ctx.lineTo(padding + cell * (size - 1), pos);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(pos, padding);
    ctx.lineTo(pos, padding + cell * (size - 1));
    ctx.stroke();
  }

  // Star points (hoshi)
  ctx.fillStyle = theme.starPointColor;
  for (const sp of getStarPoints(size)) {
    const sx = padding + sp.x * cell;
    const sy = padding + sp.y * cell;
    ctx.beginPath();
    ctx.arc(sx, sy, Math.max(2.5, cell * 0.08), 0, Math.PI * 2);
    ctx.fill();
  }

  // Stones
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const point = state[y]?.[x];
      if (!point) continue;
      const cx = padding + x * cell;
      const cy = padding + y * cell;
      ctx.beginPath();
      ctx.arc(cx, cy, cell * 0.42, 0, Math.PI * 2);
      ctx.fillStyle = point === 1 ? theme.blackStone : theme.whiteStone;
      ctx.fill();
      ctx.strokeStyle = theme.stoneStroke;
      ctx.lineWidth = 1;
      ctx.stroke();
    }
  }

  // SGF markup (circle/square/triangle/mark/label)
  for (const mark of marks ?? []) {
    if (mark.x < 0 || mark.x >= size || mark.y < 0 || mark.y >= size) continue;
    const mx = padding + mark.x * cell;
    const my = padding + mark.y * cell;
    const onStone = state[mark.y]?.[mark.x];
    const color = onStone === 1 ? theme.lastMoveMarkerOnBlack : onStone === 2 ? theme.lastMoveMarkerOnWhite : theme.markerColor;
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = Math.max(1.5, cell * 0.06);

    if (mark.type === 'circle') {
      ctx.beginPath();
      ctx.arc(mx, my, cell * 0.28, 0, Math.PI * 2);
      ctx.stroke();
    } else if (mark.type === 'square') {
      const half = cell * 0.24;
      ctx.strokeRect(mx - half, my - half, half * 2, half * 2);
    } else if (mark.type === 'triangle') {
      const r = cell * 0.3;
      ctx.beginPath();
      ctx.moveTo(mx, my - r);
      ctx.lineTo(mx + r * 0.87, my + r * 0.5);
      ctx.lineTo(mx - r * 0.87, my + r * 0.5);
      ctx.closePath();
      ctx.stroke();
    } else if (mark.type === 'mark') {
      const r = cell * 0.22;
      ctx.beginPath();
      ctx.moveTo(mx - r, my - r);
      ctx.lineTo(mx + r, my + r);
      ctx.moveTo(mx + r, my - r);
      ctx.lineTo(mx - r, my + r);
      ctx.stroke();
    } else if (mark.type === 'label') {
      ctx.font = `${Math.round(cell * 0.5)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(mark.text ?? '', mx, my);
    }
  }

  // Last move (circle marker)
  if (lastMove && state[lastMove.y]?.[lastMove.x]) {
    const lcx = padding + lastMove.x * cell;
    const lcy = padding + lastMove.y * cell;
    const stoneColor = state[lastMove.y]?.[lastMove.x];
    ctx.beginPath();
    ctx.arc(lcx, lcy, cell * 0.2, 0, Math.PI * 2);
    ctx.strokeStyle = stoneColor === 1 ? theme.lastMoveMarkerOnBlack : theme.lastMoveMarkerOnWhite;
    ctx.lineWidth = Math.max(1.5, cell * 0.05);
    ctx.stroke();
  }

  // Cursor / stone preview (hover)
  if (hover && hover.x >= 0 && hover.x < size && hover.y >= 0 && hover.y < size) {
    const hcx = padding + hover.x * cell;
    const hcy = padding + hover.y * cell;
    if (!state[hover.y]?.[hover.x]) {
      ctx.beginPath();
      ctx.arc(hcx, hcy, cell * 0.42, 0, Math.PI * 2);
      if (turn === 1) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
        ctx.fill();
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.6)';
        ctx.lineWidth = 1;
        ctx.stroke();
      } else {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
        ctx.fill();
        ctx.strokeStyle = 'rgba(60, 60, 60, 0.6)';
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    } else {
      // Highlight ring over the pointed-at existing stone
      ctx.beginPath();
      ctx.arc(hcx, hcy, cell * 0.46, 0, Math.PI * 2);
      ctx.strokeStyle = theme.hoverHighlight;
      ctx.lineWidth = Math.max(2, cell * 0.06);
      ctx.stroke();
    }
  }
}
